<section class="document-assets" aria-labelledby="document-assets-title">
  <header>
    <div>
      <h2 id="document-assets-title">Document assets</h2>
      <p class="muted">Immutable images and reusable brand profiles</p>
    </div>
  </header>
  @if (!canRead() || accessLost) {
    <ui-standard-state
      kind="forbidden"
      message="Document assets are unavailable with your current access."
      recoveryLabel="Check access"
      (recovery)="checkAccess()"
    />
  } @else {
    <div class="tools">
      <label
        >Owner scope<select
          uiControl
          name="asset-scope"
          [ngModel]="scopeKey"
          (ngModelChange)="changeScope($event)"
          [disabled]="busy || loading"
        >
          @for (item of catalog?.scopes ?? []; track key(item)) {
            <option [value]="key(item)">{{ item.label }}</option>
          }
        </select></label
      >
      <label
        >Find an asset or profile<input
          uiControl
          name="asset-search"
          type="search"
          [(ngModel)]="search"
          [disabled]="busy"
          maxlength="120"
      /></label>
      <button
        uiButton="secondary"
        type="button"
        (click)="load()"
        [disabled]="busy || loading || state.dirty()"
      >
        Refresh
      </button>
      <button
        uiButton
        type="button"
        (click)="beginAsset()"
        [disabled]="!canUpload"
      >
        Upload image
      </button>
      <button
        uiButton="secondary"
        type="button"
        (click)="beginBrand()"
        [disabled]="!canUpload"
      >
        Create brand profile
      </button>
      <label class="import-label"
        >Import asset manifest<input
          uiControl
          type="file"
          name="asset-manifest"
          accept="application/json,.json"
          [disabled]="!canUpload"
          (change)="importFile($event)"
      /></label>
    </div>
    @if (error) {
      <p role="alert">{{ error }}</p>
    }
    @if (notice) {
      <p role="status">{{ notice }}</p>
    }
    @if (loading || busy) {
      <ui-standard-state
        kind="loading"
        [message]="
          busy ? 'Confirming the asset operation…' : 'Loading document assets…'
        "
      />
    }
    @if (catalog) {
      <p class="muted limits">
        PNG/JPEG · up to {{ catalog.limits.maxBytes / 1024 / 1024 }} MiB ·
        {{ catalog.limits.maxWidthPx }} × {{ catalog.limits.maxHeightPx }} px
        maximum · {{ catalog.limits.maxPixels | number }} decoded pixels. Files
        are validated and scanned before selection.
      </p>
      @if (editor) {
        <form
          class="asset-editor"
          (ngSubmit)="save()"
          [attr.aria-label]="
            editor === 'asset'
              ? 'Upload immutable image version'
              : 'Save immutable brand profile'
          "
        >
          <h3>
            {{
              editor === "asset"
                ? replacingAsset
                  ? "Replace image with a new version"
                  : "Upload image"
                : replacingBrand
                  ? "Create new brand profile version"
                  : "Create brand profile"
            }}
          </h3>
          <fieldset [disabled]="busy || !canUpload">
            <label
              >Name<input
                uiControl
                name="asset-name"
                [(ngModel)]="name"
                (ngModelChange)="changed()"
                maxlength="160"
                required
            /></label>
            @if (editor === "asset") {
              <label
                >Meaningful alternative text<input
                  uiControl
                  name="asset-alternative-text"
                  [(ngModel)]="alternativeText"
                  (ngModelChange)="changed()"
                  maxlength="300"
                  required
              /></label>
              <label
                >PNG or JPEG file<input
                  uiControl
                  name="asset-file"
                  type="file"
                  accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                  (change)="selectFile($event)"
                  required
              /></label>
              @if (file) {
                <p>{{ file.name }} · {{ file.size | number }} bytes</p>
              }
              @if (replacingAsset) {
                <p class="muted">
                  New version of {{ replacingAsset.reference.assetId }} ·
                  current version {{ replacingAsset.reference.version }}.
                  Existing compositions retain their selected version.
                </p>
              }
            } @else {
              <label
                >Embedded font<select
                  uiControl
                  name="brand-font"
                  [(ngModel)]="style.fontCode"
                  (ngModelChange)="changed()"
                >
                  @for (font of catalog.fonts; track font.code) {
                    <option [value]="font.code" [disabled]="!font.available">
                      {{ font.label
                      }}{{ font.available ? "" : " · Unavailable" }}
                    </option>
                  }
                </select></label
              >
              <label
                >Logo version<select
                  uiControl
                  name="brand-logo"
                  [ngModel]="logoKey"
                  (ngModelChange)="chooseLogo($event)"
                >
                  <option value="">None</option>
                  @for (
                    item of catalog.items;
                    track item.reference.assetId + ":" + item.reference.version
                  ) {
                    <option [value]="referenceKey(item)">
                      {{ item.name }} · v{{ item.reference.version
                      }}{{ item.isLatest ? "" : " · Retained" }}
                    </option>
                  }
                </select></label
              >
              <div class="paired">
                <label
                  >Logo width (pt)<input
                    uiControl
                    name="brand-logo-width"
                    type="number"
                    [(ngModel)]="style.logoWidthPt"
                    (ngModelChange)="changed()"
                    min="12"
                    max="144"
                    required /></label
                ><label
                  >Logo height (pt)<input
                    uiControl
                    name="brand-logo-height"
                    type="number"
                    [(ngModel)]="style.logoHeightPt"
                    (ngModelChange)="changed()"
                    min="12"
                    max="144"
                    required
                /></label>
              </div>
              <p class="muted">
                Images fit inside this box with their original proportions.
              </p>
              <label
                >Logo alignment<select
                  uiControl
                  name="brand-logo-alignment"
                  [(ngModel)]="style.logoAlignment"
                  (ngModelChange)="changed()"
                >
                  <option value="start">Start</option>
                  <option value="center">Center</option>
                  <option value="end">End</option>
                </select></label
              >
              <label
                >Accent color<input
                  uiControl
                  name="brand-accent"
                  type="color"
                  [(ngModel)]="style.accentColor"
                  (ngModelChange)="changed()"
              /></label>
              <label
                >Watermark wording<input
                  uiControl
                  name="brand-watermark-text"
                  [(ngModel)]="style.watermarkText"
                  (ngModelChange)="changed()"
                  maxlength="80"
              /></label>
              <label
                >Watermark color<input
                  uiControl
                  name="brand-watermark-color"
                  type="color"
                  [(ngModel)]="style.watermarkColor"
                  (ngModelChange)="changed()"
              /></label>
              <div class="paired">
                <label
                  >Watermark opacity<input
                    uiControl
                    name="brand-watermark-opacity"
                    type="number"
                    [(ngModel)]="style.watermarkOpacity"
                    (ngModelChange)="changed()"
                    min="0"
                    max="1"
                    step="0.01"
                    required /></label
                ><label
                  >Watermark rotation (°)<input
                    uiControl
                    name="brand-watermark-rotation"
                    type="number"
                    [(ngModel)]="style.watermarkRotation"
                    (ngModelChange)="changed()"
                    min="-90"
                    max="90"
                    required
                /></label>
              </div>
              <p class="muted">
                Profiles retain the exact font and logo hashes. Uploaded fonts,
                remote resources and executable content are unavailable.
              </p>
            }
          </fieldset>
          <div class="actions">
            <button uiButton type="submit" [disabled]="!canSave">
              {{
                editor === "asset"
                  ? "Upload immutable version"
                  : "Save immutable profile"
              }}</button
            ><button
              uiButton="secondary"
              type="button"
              (click)="cancel()"
              [disabled]="busy"
            >
              Cancel
            </button>
          </div>
        </form>
      }
      <section aria-label="Image versions">
        <h3>Images</h3>
        @if (assetRows.length) {
          <ui-grid
            [rowData]="assetRows"
            [columnDefs]="assetColumns"
            [agGridOptions]="gridOptions"
            [showPanelHeader]="false"
            [showBlankRowsOnLastPage]="false"
            [autoHidePaginator]="true"
          />
        } @else {
          <ui-standard-state
            kind="empty"
            message="No image versions match this scope and search."
          />
        }
      </section>
      <section aria-label="Brand profile versions">
        <h3>Brand profiles</h3>
        @if (brandRows.length) {
          <ui-grid
            [rowData]="brandRows"
            [columnDefs]="brandColumns"
            [agGridOptions]="gridOptions"
            [showPanelHeader]="false"
            [showBlankRowsOnLastPage]="false"
            [autoHidePaginator]="true"
          />
        } @else {
          <ui-standard-state
            kind="empty"
            message="No brand profile versions match this scope and search."
          />
        }
      </section>
      <p class="muted">
        Shared versions keep their owner. Create a profile in the selected scope
        for a local alternative; compositions select exact versions. Historical
        versions cannot be deleted. Asset manifests support compatibility
        checks; whole-pack activation and promotion are separate workflows.
      </p>
    }
  }
</section>
